/**
 *
 * UserConsent
 *
 */

import React, { memo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFirebase } from 'react-redux-firebase';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Typography from '@material-ui/core/Typography';
import ButtonBase from '@material-ui/core/ButtonBase';
import Fade from '@material-ui/core/Fade';
import _get from 'lodash/get';
import _classNames from 'classnames';
import { showPrivacyPolicy } from 'containers/Main/actions';
import { makeSelectAuth, makeSelectProfile } from 'containers/Auth/selectors';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { makeSelectTextDirection } from 'containers/Main/selectors';
import isBefore from 'date-fns/isBefore';
import useCookie from './hooks/useCookie';

const useStyles = makeStyles(theme => ({
  wrapper: {
    position: 'fixed',
    padding: '0 18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    left: 0,
    right: 0,
    bottom: 24,
  },
  wrapperBottomWithNav: {
    [theme.breakpoints.down('xs')]: {
      bottom: 80,
    },
  },
  container: {
    backgroundColor: 'white',
    color: '#4B4B4B',
    padding: '0.75em 1em',
    borderRadius: 8,
    border: '1px solid #EDEDED',
    maxWidth: '100%',
    boxShadow: '0 9px 18px 0 rgba(0,0,0,0.06)',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    overflow: 'hidden',
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
    opacity: ({ opacity }) => opacity,
    transition: 'all 2s ease-in-out',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    [theme.breakpoints.down('xs')]: {
      gap: '16px',
      padding: '10px',
    },
  },
  text: {
    fontSize: 14,
    letterSpacing: 0,
    color: '#4B4B4B',
    lineHeight: '18px',
    marginRight: '1em',
    flex: '1',
    [theme.breakpoints.down('xs')]: {
      margin: 0,
      textAlign: 'justify',
    },
    direction: ({ textDirection }) => textDirection,
  },
  textLink: {
    cursor: 'pointer',
  },
  button: {
    borderRadius: 5,
    backgroundColor: '#FFE3AB',
    color: '#000',
    padding: 4,
    fontSize: 12,
    lineHeight: '18px',
    fontWeight: '600',
    minWidth: 62,
    [theme.breakpoints.down('xs')]: {
      padding: '8px 16px',
      whiteSpace: 'nowrap',
      marginLeft: 'auto',
    },
    direction: ({ textDirection }) => textDirection,
  },
}));

const UserConsent = memo(() => {
  const textDirection = useSelector(makeSelectTextDirection());
  const classes = useStyles({ textDirection });
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xs'));
  const auth = useSelector(makeSelectAuth());
  const profile = useSelector(makeSelectProfile());
  const firebase = useFirebase();
  const [status, setStatus] = useCookie('cookieconsent_status');
  const [privacy, setPrivacy] = useCookie('cookieconsent_privacy');
  const [definitiveText, setDefinitiveText] = useState(null);
  const dispatch = useDispatch();
  const [siteCopy] = useSiteCopySelector(['cookie-consent']);

  useEffect(() => setDefinitiveText(null), [auth, profile]);

  const createdAt = !auth.isEmpty
    ? new Date(Number(auth.createdAt))
    : Date.now();
  const privacyUpdated = new Date(2020, 2, 6);
  const shouldShowByDate = isBefore(createdAt, privacyUpdated);
  const visible =
    !definitiveText &&
    (status !== 'dismiss' ||
      (!auth.isEmpty &&
        shouldShowByDate &&
        !profile.privacyConsent &&
        privacy !== 'dismiss'));

  const openPrivacyPolicy = () => dispatch(showPrivacyPolicy(true));

  const COOKIE_CONSENT = (
    <Typography variant="body2" className={classes.text}>
      {_get(siteCopy, 'pageCopy.label')}
    </Typography>
  );

  const PRIVACY_CONSENT = (
    <Typography variant="body2" className={classes.text}>
      {_get(siteCopy, 'pageCopy.privacyLabel1')}{' '}
      <b
        className={classes.textLink}
        role="button"
        tabIndex={0}
        onClick={openPrivacyPolicy}
      >
        {_get(siteCopy, 'pageCopy.privacyLabel2')}
      </b>{' '}
      {_get(siteCopy, 'pageCopy.privacyLabel3')}
    </Typography>
  );

  if (!auth.isLoaded || !profile.isLoaded) return null;

  const text =
    definitiveText || (status !== 'dismiss' ? COOKIE_CONSENT : PRIVACY_CONSENT);

  const clickHandler =
    status !== 'dismiss'
      ? () => {
          setDefinitiveText(COOKIE_CONSENT);
          setStatus('dismiss', {
            expires: 365,
          });
        }
      : () => {
          setDefinitiveText(PRIVACY_CONSENT);
          setPrivacy('dismiss', {
            expires: 365,
          });
          firebase.updateProfile({ privacyConsent: true });
        };

  return (
    <Fade in={visible} timeout={1000}>
      <div
        className={_classNames(classes.wrapper, {
          [classes.wrapperBottomWithNav]: isXs,
        })}
      >
        <div className={classes.container}>
          {text}
          <ButtonBase className={classes.button} onClick={clickHandler}>
            {_get(siteCopy, 'pageCopy.buttonLabel')}
          </ButtonBase>
        </div>
      </div>
    </Fade>
  );
});

export default UserConsent;
